import { useEffect, useRef } from 'react';

const steps = [
  {
    num: '01',
    icon: 'ri-phone-line',
    title: 'お問い合わせ',
    desc: 'フォームまたはお電話にてお気軽にご連絡ください。お品物の種類や点数を簡単にお知らせいただくだけでOKです。',
  },
  {
    num: '02',
    icon: 'ri-calendar-check-line',
    title: '日程調整',
    desc: 'ご都合の良い日時・場所をご確認し、出張・催事の日程を調整いたします。お客様のペースに合わせて対応します。',
  },
  {
    num: '03',
    icon: 'ri-map-pin-line',
    title: 'ご自宅・催事へ伺います',
    desc: '指定の場所へスタッフが直接伺います。出張費・査定費は一切無料。お品物を梱包・搬出する必要もありません。',
  },
  {
    num: '04',
    icon: 'ri-search-eye-line',
    title: '無料査定',
    desc: '経験豊富なスタッフがその場で丁寧に査定します。納得いただけるまで何でもご質問ください。押しつけは一切ありません。',
  },
  {
    num: '05',
    icon: 'ri-hand-coin-line',
    title: '現金即払い',
    desc: '査定金額にご納得いただけましたら、その場で現金にてお支払いいたします。銀行振込にも対応しています。',
  },
];

export default function ProcessSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.section-reveal').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="process" ref={sectionRef} className="py-24 md:py-32 bg-kura-cream">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16 section-reveal">
          <span
            className="text-kura-gold text-xs tracking-[0.4em] uppercase"
            style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
          >
            How It Works
          </span>
          <h2
            className="text-kura-green text-3xl md:text-4xl font-bold mt-2 tracking-wide"
            style={{ fontFamily: 'Noto Serif JP, serif' }}
          >
            買取の流れ
          </h2>
          <div className="ainu-border mt-4 max-w-xs mx-auto" />
          <p
            className="text-kura-dark/60 text-sm mt-4 tracking-wide"
            style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
          >
            催事・出張買取のプロセスはとても簡単です。
          </p>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Connecting line (desktop) */}
          <div className="hidden md:block absolute top-10 left-[10%] right-[10%] h-px bg-kura-green/20" />

          <div className="grid md:grid-cols-5 gap-6">
            {steps.map((step, index) => (
              <div key={step.num} className="section-reveal flex flex-col items-center text-center gap-4">
                {/* Step circle */}
                <div className="relative">
                  <div className="w-20 h-20 rounded-full bg-kura-green flex flex-col items-center justify-center border-2 border-kura-gold/50 relative z-10">
                    <i className={`${step.icon} text-kura-gold text-xl`} />
                  </div>
                  <span
                    className="absolute -top-2 -right-2 bg-kura-gold text-kura-dark text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center"
                    style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                  >
                    {index + 1}
                  </span>
                </div>

                {/* Content */}
                <div>
                  <div
                    className="text-kura-gold text-xs font-bold mb-1 tracking-widest"
                    style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                  >
                    {step.num}
                  </div>
                  <h3
                    className="text-kura-green font-semibold text-sm mb-2 tracking-wide"
                    style={{ fontFamily: 'Noto Serif JP, serif' }}
                  >
                    {step.title}
                  </h3>
                  <p
                    className="text-kura-dark/65 text-xs leading-relaxed tracking-wide"
                    style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
                  >
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Note box */}
        <div className="mt-16 section-reveal bg-kura-green rounded-lg p-6 border border-kura-gold/30">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-kura-gold/20 rounded-full flex-shrink-0 mt-1">
              <i className="ri-information-line text-kura-gold text-xl" />
            </div>
            <div>
              <h3
                className="text-kura-gold-light text-lg font-semibold mb-2 tracking-wide"
                style={{ fontFamily: 'Noto Serif JP, serif' }}
              >
                出張・催事について
              </h3>
              <p
                className="text-white/85 text-sm leading-relaxed tracking-wide"
                style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
              >
                当社は実店舗を持たず、催事会場やご自宅・指定場所への出張買取を主な営業形態としています。
                北海道を中心に対応しておりますが、遠方の場合もお気軽にご相談ください。
                出張費・査定費はすべて無料です。キャンセルも一切費用はかかりません。
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
